import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import './App.css';

// Import components
import SubscriptionPlans from './components/SubscriptionManager';
import AdminDashboard from './components/AdminDashboard';
import PasswordChange from './components/PasswordChange';
import CarTransfer from './components/CarTransfer';
import ServiceReminders from './components/ServiceReminders';
import UserSettings from './components/Settings';
import CarPhotoGallery from './components/CarPhotoGallery';
import FuelFillUp from './components/FuelFillUp';
import PartsList from './components/PartsList';
import ShareLinkModal from './components/ShareLinkModal';
import Logo from './components/Logo';

// Import measurement utilities
import { formatDistance, getDistanceUnit, getDistanceUnitLong } from './utils/measurements';

// Import shadcn components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './components/ui/alert-dialog';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Icons
import { Car, Plus, Wrench, Calendar, MapPin, DollarSign, Clock, Edit, Trash2, Share, Users, Copy, UserPlus, BarChart3, Camera, Upload, X, Image, LogOut, User, Crown, Settings, Gift, CreditCard, Star, Shield, Lock, Send, Check, Link2 } from 'lucide-react';
import { getBackendUrl, getApiUrl, isDevEnvironment } from './utils/config';
import { applyTextVisibilityFix, setupTextVisibilityObserver, setupContinuousTextFix } from './utils/textVisibilityFix';

const BACKEND_URL = getBackendUrl();
const API = getApiUrl();

// Enable credentials for all axios requests (required for session cookies)
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = React.createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const oauthProcessed = useRef(false);  // Guard against double-processing OAuth

  useEffect(() => {
    // Check for OAuth session_id in URL fragment first
    const fragment = window.location.hash;
    if (fragment && fragment.includes('session_id=')) {
      // Guard against double-processing (React StrictMode, etc.)
      if (oauthProcessed.current) {
        console.log('OAuth callback already processed, skipping...');
        setLoading(false);
        return;
      }
      oauthProcessed.current = true;
      handleOAuthCallback();
      return;
    }

    // Check for existing token (JWT auth)
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else if (user) {
      // OAuth user - try to fetch profile using session cookie
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const handleOAuthCallback = async () => {
    try {
      const fragment = window.location.hash.substring(1);
      const params = new URLSearchParams(fragment);
      const sessionId = params.get('session_id');
      
      console.log('OAuth Callback - Fragment:', fragment);
      console.log('OAuth Callback - Session ID:', sessionId);
      
      if (!sessionId) {
        console.error('OAuth Callback - No session_id found in URL');
        setLoading(false);
        return;
      }

      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      // Call backend to process OAuth session
      console.log('OAuth Callback - Calling backend...');
      const response = await axios.post(`${API}/auth/oauth/session`, null, {
        headers: {
          'X-Session-ID': sessionId
        },
        withCredentials: true  // Important for cookies
      });

      console.log('OAuth Callback - Backend response:', response.data);
      
      // Set user data and persist to localStorage
      const userData = response.data.user;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Clean URL fragment
      window.history.replaceState(null, '', window.location.pathname);
      
      toast.success('Welcome to Track My Garage!');
    } catch (error) {
      console.error('OAuth callback error:', error);
      console.error('OAuth callback error response:', error.response?.data);
      const errorMessage = error.response?.data?.detail || 'Authentication failed. Please try again.';
      toast.error(errorMessage);
      window.history.replaceState(null, '', window.location.pathname);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const currentToken = token || localStorage.getItem('token');
      const headers = currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {};
      
      // Include withCredentials for session cookie authentication (OAuth users)
      const response = await axios.get(`${API}/auth/me`, { headers, withCredentials: true });
      const userData = response.data;
      
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token } = response.data;
    
    // Store token in localStorage first
    localStorage.setItem('token', access_token);
    
    // Set token state
    setToken(access_token);
    
    // Set axios default headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Fetch user profile (this will also set loading to false)
    await fetchUserProfile();
    
    return response.data;
  };

  const signup = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/signup`, { name, email, password });
    const { access_token } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    await fetchUserProfile();
    return response.data;
  };

  const logout = async () => {
    try {
      // Call backend logout to clear session
      await axios.post(`${API}/auth/logout`, null, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, loading, token }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication Components
const LoginForm = ({ onSwitchToSignup, onSwitchToForgotPassword }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl text-center"> Welcome Back to TMG</CardTitle>
        <CardDescription className="text-center">
          Sign in to your Track My Garage account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              data-testid="login-email-input"
            />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password">Password</Label>
              <Button 
                type="button"
                variant="link" 
                className="text-xs text-blue-600 hover:text-blue-700 p-0 h-auto"
                onClick={(e) => {
                  e.preventDefault();
                  onSwitchToForgotPassword();
                }}
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Button>
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              data-testid="login-password-input"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
            {loading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>
        
        {/* Google Sign-In temporarily disabled
        <div className="mt-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">Or continue with</span>
            </div>
          </div>
          
          <Button
            type="button"
            variant="outline"
            className="w-full mt-4"
            onClick={() => {
              const redirectUrl = encodeURIComponent(window.location.origin);
              window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
            }}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </Button>
        </div>
        */}
        
        <div className="mt-4 text-center">
          <Button variant="link" onClick={onSwitchToSignup} data-testid="switch-to-signup-btn">
            Don't have an account? Sign up
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Emergency Admin Login Component - DISABLED FOR SECURITY
/*
const AdminLoginForm = () => {
  // Component disabled for security reasons
  return null;
};
*/

const SignupForm = ({ onSwitchToLogin }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await signup(name, email, password);
      toast.success('Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl text-center">Create Account</CardTitle>
        <CardDescription className="text-center">
          Join Track My Garage to track your vehicles
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              required
              data-testid="signup-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              data-testid="signup-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              data-testid="signup-password-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              data-testid="signup-confirm-password-input"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-btn">
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <Button variant="link" onClick={onSwitchToLogin} data-testid="switch-to-login-btn">
            Already have an account? Sign in
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ForgotPasswordPage = () => {
  // Check if email is passed via URL params
  const urlParams = new URLSearchParams(window.location.search);
  const emailFromUrl = urlParams.get('email') || '';
  
  const [step, setStep] = useState(emailFromUrl ? 2 : 1); // Start at step 2 if email is provided
  const [email, setEmail] = useState(emailFromUrl);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setStep(2);
      toast.success('6-digit reset code sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!code || code.length !== 6) {
      toast.error('Please enter the 6-digit code from your email');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password-with-code`, {
        email,
        code,
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="giant" />
          </div>
          <p className="text-gray-600">Track your vehicle maintenance like a pro</p>
        </div>

        <Card className="w-full max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl text-center">
              {step === 1 ? 'Forgot Password?' : 'Reset Your Password'}
            </CardTitle>
            <CardDescription className="text-center">
              {step === 1 
                ? "Enter your email and we'll send you a 6-digit reset code"
                : "Enter the code from your email and your new password"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={handleSendCode} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    data-testid="forgot-password-email-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-password-submit-btn">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code">6-Digit Code</Label>
                  <Input
                    id="code"
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength={6}
                    required
                    className="text-center text-2xl font-mono tracking-widest"
                    data-testid="reset-code-input"
                  />
                  <p className="text-xs text-gray-500 text-center">
                    Code sent to {email}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    required
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-submit-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <Button 
                  type="button"
                  variant="link" 
                  className="w-full"
                  onClick={() => setStep(1)}
                >
                  ← Resend Code
                </Button>
              </form>
            )}
            <div className="mt-4 text-center">
              <Button 
                variant="link" 
                onClick={() => window.location.href = '/'}
                data-testid="back-to-login-btn"
              >
                ← Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ForgotPasswordForm = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('6-digit reset code sent! Check your email.');
      // Redirect to the full forgot password page with email pre-filled
      setTimeout(() => {
        window.location.href = `/forgot-password?email=${encodeURIComponent(email)}`;
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl text-center">Forgot Password?</CardTitle>
        <CardDescription className="text-center">
          Enter your email and we'll send you a 6-digit reset code
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              data-testid="forgot-password-email-input"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-password-submit-btn">
            {loading ? 'Sending...' : 'Send Reset Code'}
          </Button>
        </form>
        <div className="mt-4 text-center">
          <Button 
            variant="link" 
            onClick={onBackToLogin}
            data-testid="back-to-login-btn"
          >
            ← Back to Login
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ResetPasswordPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Get token from URL
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      toast.error('Invalid reset link');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="giant" />
          </div>
          <p className="text-gray-600">Track your vehicle maintenance like a pro</p>
        </div>

        <Card className="w-full max-w-md mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Reset Password</CardTitle>
            <CardDescription className="text-center">
              {success ? "Your password has been reset!" : "Enter your new password"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!success ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    required
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-submit-btn">
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <Check className="w-16 h-16 text-green-600 mx-auto" />
                <p className="text-gray-600">
                  Redirecting to login...
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AuthPage = () => {
  const [currentView, setCurrentView] = useState('login'); // 'login', 'signup', 'forgot-password'

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="giant" />
          </div>
          <p className="text-gray-600">Track your vehicle maintenance like a pro</p>
        </div>

        {currentView === 'login' && (
          <LoginForm 
            onSwitchToSignup={() => setCurrentView('signup')} 
            onSwitchToForgotPassword={() => setCurrentView('forgot-password')}
          />
        )}
        {currentView === 'signup' && (
          <SignupForm onSwitchToLogin={() => setCurrentView('login')} />
        )}
        {currentView === 'forgot-password' && (
          <ForgotPasswordForm onBackToLogin={() => setCurrentView('login')} />
        )}
      </div>
    </div>
  );
};

// Header Component with role display and user menu
const Header = ({ onSettingsChange }) => {
  const { user, logout } = useAuth();

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-yellow-600" />;
      case 'admin': return <Shield className="w-4 h-4 text-blue-600" />;
      default: return <User className="w-4 h-4 text-gray-600" />;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner': return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Owner</Badge>;
      case 'admin': return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Admin</Badge>;
      default: return null;
    }
  };

  return (
    <div className="bg-white shadow-sm border-b">
      <div className="container mx-auto px-4 py-2">
        <div className="flex justify-between items-center">
          <Logo size="xl" />
          
          {/* Mobile-optimized user section */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* User info - responsive layout */}
            <div className="flex items-center gap-2">
              {getRoleIcon(user?.role)}
              <div className="flex flex-col items-end">
                <span className="text-xs md:text-sm text-gray-700 truncate max-w-[120px] md:max-w-none">
                  {user?.name}
                </span>
                <div className="flex items-center gap-1">
                  {getRoleBadge(user?.role)}
                  {user?.subscription && (
                    <div className="flex items-center gap-1 text-xs">
                      <Star className="w-3 h-3 text-green-600" />
                      <span className="text-green-600">{user.subscription.plan_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* User Menu - responsive button */}
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-gray-200 text-gray-700 hover:bg-gray-50 px-2 md:px-3"
                  data-testid="user-menu-btn"
                >
                  <Settings className="w-4 h-4 md:mr-2" />
                  <span className="hidden md:inline">Account</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    Account Settings
                  </DialogTitle>
                  <DialogDescription>
                    Manage your account preferences and security
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getRoleIcon(user?.role)}
                      <div>
                        <div className="font-medium">{user?.name}</div>
                        <div className="text-sm text-gray-600">{user?.email}</div>
                        <div className="flex items-center gap-2 mt-1">
                          {getRoleBadge(user?.role)}
                          {user?.subscription && (
                            <Badge variant="outline" className="text-xs">
                              {user.subscription.plan_name}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <PasswordChange 
                      user={user}
                      trigger={
                        <Button 
                          variant="outline" 
                          className="w-full justify-start border-blue-200 text-blue-700 hover:bg-blue-50"
                          data-testid="password-change-trigger"
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Change Password
                        </Button>
                      }
                    />
                    
                    <UserSettings 
                      user={user}
                      onSettingsChange={onSettingsChange}
                      trigger={
                        <Button 
                          variant="outline" 
                          className="w-full justify-start border-green-200 text-green-700 hover:bg-green-50"
                          data-testid="app-settings-trigger"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          App Settings
                        </Button>
                      }
                    />
                    
                    <Button
                      variant="outline"
                      className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50"
                      onClick={logout}
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
};

// Photo Upload Component (same as before)
const PhotoUpload = ({ onPhotoSelect, existingPhoto = null, className = "", multiple = false }) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(existingPhoto?.data ? `data:${existingPhoto.content_type};base64,${existingPhoto.data}` : null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      const base64 = await convertToBase64(file);
      const photoData = {
        filename: file.name,
        data: base64.split(',')[1], // Remove data:image/jpeg;base64, prefix
        content_type: file.type,
        size: file.size
      };

      setPreview(base64);
      onPhotoSelect(photoData);
    } catch (error) {
      toast.error('Failed to process image');
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const removePhoto = () => {
    setPreview(null);
    onPhotoSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {preview ? (
        <div className="relative">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-full h-32 object-cover rounded-lg border"
          />
          <Button
            onClick={removePhoto}
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            data-testid="remove-photo-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          data-testid="photo-upload-area"
        >
          <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            Drop image here or click to select
          </p>
          <p className="text-xs text-gray-400 mt-1">
            JPG, PNG up to 5MB
          </p>
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
        data-testid="photo-input"
      />
    </div>
  );
};

// Car Dashboard Component with subscription limits
const CarDashboard = ({ cars, onAddCar, onSelectCar, onJoinCar, onShowSubscriptions, onShowAdmin, user, userPreferences, sharingRequests, onRequestResponse }) => {
  const [shareCode, setShareCode] = useState('');
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const fetchHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/health`);
      const data = await res.json();
      setHealthData(data);
    } catch (err) {
      setHealthData({ status: 'unreachable', error: err.message });
    } finally {
      setHealthLoading(false);
    }
  };

  const handleJoinCar = async () => {
    if (!shareCode.trim()) {
      toast.error('Please enter a share code');
      return;
    }

    try {
      await onJoinCar({ share_code: shareCode.trim() });
      setShareCode('');
    } catch (error) {
      console.error('Error joining car:', error);
    }
  };

  const handleAddCar = () => {
    const subscription = user?.subscription;
    const maxCars = subscription?.max_cars ?? 1;
    
    // Check if user has reached their car limit (skip if unlimited: -1)
    if (maxCars !== -1 && cars.length >= maxCars) {
      toast.error(`You've reached the limit of ${maxCars} car(s) on the ${subscription?.plan_name || 'Free'} plan. Please upgrade your subscription.`);
      onShowSubscriptions();
      return;
    }
    onAddCar();
  };

  const getSubscriptionStatus = () => {
    const subscription = user?.subscription;
    if (!subscription?.has_subscription) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Gift className="w-5 h-5 text-yellow-600 mr-2" />
              <div>
                <h3 className="font-medium text-yellow-800">Free Plan</h3>
                <p className="text-sm text-yellow-600">Limited to {subscription?.max_cars} car(s)</p>
              </div>
            </div>
            <Button onClick={onShowSubscriptions} className="bg-yellow-600 hover:bg-yellow-700">
              <Star className="w-4 h-4 mr-2" />
              Upgrade
            </Button>
          </div>
        </div>
      );
    }

    if (subscription?.end_date) {
      const daysLeft = Math.ceil((new Date(subscription.end_date) - new Date()) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 7) {
        return (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <CreditCard className="w-5 h-5 text-red-600 mr-2" />
                <div>
                  <h3 className="font-medium text-red-800">Subscription Expiring Soon</h3>
                  <p className="text-sm text-red-600">{daysLeft} days left on your {subscription.plan_name} plan</p>
                </div>
              </div>
              <Button onClick={onShowSubscriptions} className="bg-red-600 hover:bg-red-700">
                Renew
              </Button>
            </div>
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div className="space-y-6">
      {getSubscriptionStatus()}
      
      {/* Sharing Requests will be moved to a separate modal */}

      {/* Mobile-responsive dashboard header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">My Vehicles</h1>
            <p className="text-gray-600 mt-2 text-sm md:text-base">
              Manage your vehicles and track their service history 
              {user?.subscription && (
                <span className="block md:inline md:ml-2 text-sm">
                  ({cars.length}/{user.subscription.max_cars === -1 ? '∞' : user.subscription.max_cars} cars used)
                </span>
              )}
            </p>
          </div>
          
          {/* Mobile-friendly button layout */}
          <div className="flex flex-wrap gap-2">
            {isDevEnvironment() && (
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!bg-orange-500 !text-white hover:!bg-orange-600 shadow"
                    onClick={fetchHealthCheck}
                  >
                    <Shield className="w-4 h-4 md:mr-2" />
                    <span className="hidden md:inline">Health</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg mx-4">
                  <DialogHeader>
                    <DialogTitle>API Health Check</DialogTitle>
                    <DialogDescription>
                      Backend: {getBackendUrl()}
                    </DialogDescription>
                  </DialogHeader>
                  {healthLoading ? (
                    <p className="text-sm text-gray-500">Checking...</p>
                  ) : healthData ? (
                    <div className="space-y-3">
                      <div className={`flex items-center gap-2 text-sm font-medium ${healthData.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                        <span className={`w-2.5 h-2.5 rounded-full ${healthData.status === 'healthy' ? 'bg-green-500' : 'bg-red-500'}`} />
                        {healthData.status?.toUpperCase()}
                      </div>
                      <pre className="bg-gray-50 rounded-lg p-3 text-xs overflow-auto max-h-64">
                        {JSON.stringify(healthData, null, 2)}
                      </pre>
                    </div>
                  ) : null}
                </DialogContent>
              </Dialog>
            )}
            {(user?.role === 'admin' || user?.role === 'owner') && (
              <Button
                variant="ghost"
                size="sm"
                className="!bg-purple-600 !text-white hover:!bg-purple-700 shadow"
                onClick={onShowAdmin}
                data-testid="admin-dashboard-btn"
              >
                <Settings className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Admin</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="!bg-green-600 !text-white hover:!bg-green-700 shadow"
              onClick={onShowSubscriptions}
              data-testid="subscription-btn"
            >
              <Star className="w-4 h-4 md:mr-2" />
              <span className="hidden sm:inline">Subscription</span>
              <span className="sm:hidden">Plan</span>
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  variant="ghost"
                  size="sm"
                  className="!bg-indigo-600 !text-white hover:!bg-indigo-700 shadow" 
                  data-testid="join-car-btn"
                >
                  <UserPlus className="w-4 h-4 md:mr-2" />
                  <span className="hidden sm:inline">Join Car</span>
                  <span className="sm:hidden">Join</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md mx-4">
                <DialogHeader>
                  <DialogTitle>Request Vehicle Access</DialogTitle>
                  <DialogDescription>
                    Enter the 10-character share code to send an access request to the vehicle owner
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="share-code">Share Code</Label>
                    <Input
                      id="share-code"
                      value={shareCode}
                      onChange={(e) => setShareCode(e.target.value)}
                      placeholder="Enter 10-character code"
                      data-testid="share-code-input"
                    />
                  </div>
                  <Button onClick={handleJoinCar} className="w-full bg-green-600 hover:bg-green-700" data-testid="join-car-submit-btn">
                    Send Access Request
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  variant="ghost"
                  size="sm"
                  className={`!bg-orange-600 !text-white hover:!bg-orange-700 shadow ${sharingRequests.length > 0 ? '!bg-orange-700' : ''}`}
                  data-testid="requests-btn"
                >
                  <Users className="w-4 h-4 md:mr-2" />
                  <span className="hidden sm:inline">Requests</span>
                  <span className="sm:hidden">Req</span>
                  {sharingRequests.length > 0 && (
                    <span className="ml-1 bg-white text-orange-600 text-xs rounded-full px-2 py-0.5 font-semibold">
                      {sharingRequests.length}
                    </span>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Sharing Requests</DialogTitle>
                  <DialogDescription>
                    Manage vehicle access requests from other users
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {sharingRequests.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No pending sharing requests</p>
                    </div>
                  ) : (
                    sharingRequests.map((request) => {
                      const [selectedPermission, setSelectedPermission] = React.useState('view');
                      
                      return (
                        <div key={request.id} className="bg-gray-50 rounded-lg p-4 border">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900">
                                  {request.requester_name} wants access to your {request.car_name}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1">
                                  <strong>Vehicle:</strong> {request.car_year} {request.car_make} {request.car_model}
                                </p>
                                <p className="text-sm text-gray-600">
                                  <strong>From:</strong> {request.requester_email}
                                </p>
                                {request.request_message && (
                                  <p className="text-sm text-gray-600 mt-2">
                                    <strong>Message:</strong> "{request.request_message}"
                                  </p>
                                )}
                                <p className="text-xs text-gray-500 mt-2">
                                  Requested {new Date(request.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t">
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`permission-${request.id}`} className="text-sm font-medium">Grant Permission:</Label>
                                <Select value={selectedPermission} onValueChange={setSelectedPermission}>
                                  <SelectTrigger id={`permission-${request.id}`} className="w-[140px] h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="view">View Only</SelectItem>
                                    <SelectItem value="edit">View & Edit</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => onRequestResponse(request.id, 'approve', selectedPermission)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => onRequestResponse(request.id, 'deny')}
                                  className="border-red-200 text-red-700 hover:bg-red-50"
                                >
                                  <X className="w-4 h-4 mr-1" />
                                  Deny
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Button 
              onClick={handleAddCar} 
              variant="ghost"
              size="sm"
              className="!bg-blue-600 !text-white hover:!bg-blue-700 shadow" 
              data-testid="add-car-btn"
            >
              <Plus className="w-4 h-4 md:mr-2" />
              <span className="hidden sm:inline">Add Vehicle</span>
              <span className="sm:hidden">Add</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Service Reminders Section */}
      <ServiceReminders onSelectCar={onSelectCar} userPreferences={userPreferences} />

      {cars.length === 0 ? (
        <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-200">
          <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No vehicles yet</h3>
          <p className="text-gray-500 mb-6">Start by adding your first vehicle or join a shared vehicle</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={handleAddCar} className="bg-blue-600 hover:bg-blue-700" data-testid="add-first-car-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Vehicle
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cars.map((car) => (
            <Card 
              key={car.id} 
              className="cursor-pointer hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500 hover:border-l-blue-600 relative overflow-hidden"
              onClick={() => onSelectCar(car)}
              data-testid={`car-card-${car.id}`}
            >
              {car.photo && (
                <div className="h-32 w-full overflow-hidden">
                  <img
                    src={`data:${car.photo.content_type};base64,${car.photo.data}`}
                    alt={`${car.make} ${car.model}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {car.shared_users && car.shared_users.length > 0 && (
                <div className="absolute top-2 right-2">
                  <Badge variant="secondary" className="text-xs">
                    <Users className="w-3 h-3 mr-1" />
                    Shared
                  </Badge>
                </div>
              )}
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Car className="w-5 h-5 text-blue-600 mr-2" />
                    <span>{car.make} {car.model}</span>
                  </div>
                  <Badge variant="outline">{car.year}</Badge>
                </CardTitle>
                <CardDescription>
                  Owner: {car.owner_name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {car.color && (
                    <div className="flex items-center text-gray-600">
                      <span className="font-medium">Color:</span>
                      <span className="ml-2">{car.color}</span>
                    </div>
                  )}
                  {car.license_plate && (
                    <div className="flex items-center text-gray-600">
                      <span className="font-medium">Plate:</span>
                      <span className="ml-2">{car.license_plate}</span>
                    </div>
                  )}
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{formatDistance(car.current_odometer, userPreferences.measurement_system)}</span>
                  </div>
                  {car.shared_users && car.shared_users.length > 0 && (
                    <div className="flex items-center text-gray-600">
                      <Users className="w-4 h-4 mr-1" />
                      <span>{car.shared_users.length} shared user{car.shared_users.length > 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Add/Edit Car Form Component with VIN support
const CarForm = ({ car = null, onSubmit, onCancel, userPreferences }) => {
  const isEditing = !!car;
  const [formData, setFormData] = useState({
    make: car?.make || '',
    model: car?.model || '',
    year: car?.year || new Date().getFullYear(),
    owner_name: car?.owner_name || '',
    name: car?.name || '',
    vin: car?.vin || '',
    color: car?.color || '',
    license_plate: car?.license_plate || '',
    current_odometer: car?.current_odometer || 0,
    engine_type: car?.engine_type || '',
    engine_size: car?.engine_size || '',
    transmission: car?.transmission || '',
    modifications: car?.modifications || [],
    photo: null
  });

  const { user } = useAuth();

  useEffect(() => {
    if (user && !car) {
      setFormData(prev => ({ ...prev, owner_name: user.name }));
    }
  }, [user, car]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoSelect = (photoData) => {
    setFormData(prev => ({ ...prev, photo: photoData }));
  };

  const validateVIN = (vin) => {
    if (!vin) return true; // VIN is optional
    const cleanVin = vin.trim().toUpperCase();
    if (cleanVin.length !== 17) return false;
    return /^[A-HJ-NPR-Z0-9]{17}$/.test(cleanVin);
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Car className="w-5 h-5 mr-2 text-blue-600" />
          {isEditing ? 'Edit Vehicle' : 'Add New Vehicle'}
        </CardTitle>
        <CardDescription>
          {isEditing ? 'Update your vehicle information' : 'Enter your vehicle information to start tracking its maintenance'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Vehicle Photo</Label>
            <PhotoUpload
              onPhotoSelect={handlePhotoSelect}
              existingPhoto={car?.photo || formData.photo}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vin">VIN (Vehicle Identification Number)</Label>
            <Input
              id="vin"
              value={formData.vin}
              onChange={(e) => handleChange('vin', e.target.value.toUpperCase())}
              placeholder="17-character VIN (optional)"
              maxLength={17}
              className={formData.vin && !validateVIN(formData.vin) ? 'border-red-300' : ''}
              data-testid="car-vin-input"
            />
            {formData.vin && !validateVIN(formData.vin) && (
              <p className="text-sm text-red-600">VIN must be exactly 17 characters (no I, O, or Q)</p>
            )}
          </div>

          {/* Car Name (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="name">Car Name (Optional)</Label>
            <Input
              id="name"
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Give your car a nickname (e.g., 'Blue Beauty', 'My Honda', 'Work Truck')"
              data-testid="car-name-input"
            />
            <p className="text-xs text-gray-500">
              💡 A friendly name to personalize your car (optional but fun!)
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="make">Make *</Label>
              <Input
                id="make"
                value={formData.make}
                onChange={(e) => handleChange('make', e.target.value)}
                placeholder="e.g., Toyota, Ford, Honda"
                required
                data-testid="car-make-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="model">Model *</Label>
              <Input
                id="model"
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                placeholder="e.g., Camry, F-150, Civic"
                required
                data-testid="car-model-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="year">Year *</Label>
              <Input
                id="year"
                type="number"
                value={formData.year}
                onChange={(e) => handleChange('year', parseInt(e.target.value))}
                min="1900"
                max={new Date().getFullYear() + 1}
                required
                data-testid="car-year-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="owner_name">Owner Name *</Label>
              <Input
                id="owner_name"
                value={formData.owner_name}
                onChange={(e) => handleChange('owner_name', e.target.value)}
                placeholder="Vehicle owner name"
                required
                data-testid="car-owner-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="color">Color</Label>
              <Input
                id="color"
                value={formData.color}
                onChange={(e) => handleChange('color', e.target.value)}
                placeholder="Vehicle color"
                data-testid="car-color-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="license_plate">License Plate</Label>
              <Input
                id="license_plate"
                value={formData.license_plate}
                onChange={(e) => handleChange('license_plate', e.target.value)}
                placeholder="License plate number"
                data-testid="car-plate-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="current_odometer">Current Odometer ({getDistanceUnitLong(userPreferences?.measurement_system)})</Label>
            <Input
              id="current_odometer"
              type="number"
              value={formData.current_odometer}
              onChange={(e) => handleChange('current_odometer', parseInt(e.target.value) || 0)}
              min="0"
              placeholder="Current mileage"
              data-testid="car-odometer-input"
            />
          </div>

          {/* Enhanced Car Details */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center mb-4">
              <Wrench className="w-5 h-5 mr-2 text-blue-600" />
              <h3 className="text-lg font-semibold text-blue-800">Engine & Technical Details</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="engine_type">Engine Type</Label>
                <Select value={formData.engine_type || ''} onValueChange={(value) => handleChange('engine_type', value)}>
                  <SelectTrigger data-testid="car-engine-type-select">
                    <SelectValue placeholder="Select engine type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inline-4">Inline-4 (I4)</SelectItem>
                    <SelectItem value="V6">V6</SelectItem>
                    <SelectItem value="V8">V8</SelectItem>
                    <SelectItem value="V12">V12</SelectItem>
                    <SelectItem value="Flat-4">Flat-4 (Boxer)</SelectItem>
                    <SelectItem value="Flat-6">Flat-6 (Boxer)</SelectItem>
                    <SelectItem value="Electric">Electric Motor</SelectItem>
                    <SelectItem value="Hybrid">Hybrid (Gas + Electric)</SelectItem>
                    <SelectItem value="Diesel">Diesel</SelectItem>
                    <SelectItem value="Rotary">Rotary (Wankel)</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="engine_size">Engine Size</Label>
                <Input
                  id="engine_size"
                  value={formData.engine_size || ''}
                  onChange={(e) => handleChange('engine_size', e.target.value)}
                  placeholder="e.g., 2.0L, 3.5L, 5.7L"
                  data-testid="car-engine-size-input"
                />
                <p className="text-xs text-gray-500">
                  Examples: 1.6L, 2.4L, 3.0L, 6.2L, or kWh for electric
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="transmission">Transmission</Label>
                <Select value={formData.transmission || ''} onValueChange={(value) => handleChange('transmission', value)}>
                  <SelectTrigger data-testid="car-transmission-select">
                    <SelectValue placeholder="Select transmission" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Manual">Manual</SelectItem>
                    <SelectItem value="Automatic">Automatic</SelectItem>
                    <SelectItem value="CVT">CVT (Continuously Variable)</SelectItem>
                    <SelectItem value="Semi-Auto">Semi-Automatic</SelectItem>
                    <SelectItem value="Dual-Clutch">Dual-Clutch (DCT/PDK)</SelectItem>
                    <SelectItem value="Single-Speed">Single-Speed (Electric)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Modifications Section */}
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center mb-4">
              <Settings className="w-5 h-5 mr-2 text-green-600" />
              <h3 className="text-lg font-semibold text-green-800">Modifications & Upgrades</h3>
            </div>
            
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="modifications_input">Add Modification</Label>
                <div className="flex gap-2">
                  <Input
                    id="modifications_input"
                    placeholder="e.g., Cold Air Intake, Exhaust System, Lowering Springs..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value && !formData.modifications.includes(value)) {
                          handleChange('modifications', [...formData.modifications, value]);
                          e.target.value = '';
                        }
                      }
                    }}
                    data-testid="car-modification-input"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('modifications_input');
                      const value = input.value.trim();
                      if (value && !formData.modifications.includes(value)) {
                        handleChange('modifications', [...formData.modifications, value]);
                        input.value = '';
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="add-modification-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  Press Enter or click + to add. Examples: Performance air filter, Sport exhaust, Coilovers, Turbo kit
                </p>
              </div>
              
              {/* Modifications List */}
              {formData.modifications && formData.modifications.length > 0 && (
                <div className="space-y-2">
                  <Label>Current Modifications:</Label>
                  <div className="space-y-2">
                    {formData.modifications.map((mod, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-white border border-green-200 rounded">
                        <span className="text-sm">{mod}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const newMods = formData.modifications.filter((_, i) => i !== index);
                            handleChange('modifications', newMods);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`remove-modification-${index}`}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button 
              type="submit" 
              className="flex-1 bg-blue-600 hover:bg-blue-700" 
              data-testid="save-car-btn"
              disabled={formData.vin && !validateVIN(formData.vin)}
            >
              {isEditing ? 'Update Vehicle' : 'Save Vehicle'}
            </Button>
            <Button 
              type="button" 
              variant="ghost"
              onClick={onCancel} 
              className="flex-1 !bg-gray-600 !text-white hover:!bg-gray-700 shadow" 
              data-testid="cancel-car-btn"
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Rest of the components remain the same (CarSharingModal, CarDetail, AddServiceForm)
// I'll include them but they're largely unchanged except for authentication context

// Car Sharing Component
const CarSharingModal = ({ car, onShare, onRemoveShare }) => {
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteNotes, setInviteNotes] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  const handleShare = async () => {
    if (!shareEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    try {
      await onShare(car.id, shareEmail, sharePermission);
      setShareEmail('');
      toast.success('Vehicle shared successfully!');
    } catch (error) {
      console.error('Error sharing car:', error);
      toast.error('Failed to share vehicle');
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingInvite(true);
    try {
      const response = await axios.post(`${API}/cars/${car.id}/invite`, {
        recipient_email: inviteEmail,
        recipient_name: inviteName || null,
        notes: inviteNotes || null
      });

      if (response.data.success) {
        toast.success('Invitation sent successfully!');
        setInviteEmail('');
        setInviteName('');
        setInviteNotes('');
      } else {
        toast.error('Failed to send invitation');
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const copyShareCode = () => {
    navigator.clipboard.writeText(car.share_code);
    toast.success('Share code copied to clipboard!');
  };

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        {/* Main Share Code Section */}
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-2">10-Character Share Code</h4>
          <div className="flex items-center gap-2">
            <code className="bg-white px-3 py-2 rounded border flex-1 font-mono text-xl tracking-wider">
              {car.share_code}
            </code>
            <Button 
              onClick={copyShareCode} 
              variant="ghost"
              size="sm" 
              className="!bg-blue-600 !text-white hover:!bg-blue-700 shadow"
              data-testid="copy-share-code-btn"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Share this 10-character code with others to give them view access to this vehicle
          </p>
        </div>

        {/* Instructions */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-2 text-gray-800">How to Share:</h4>
          <div className="space-y-2 text-sm text-gray-600">
            <p>1. <strong>Copy the share code</strong> above</p>
            <p>2. <strong>Send it</strong> to the person you want to share with (via text, email, etc.)</p>
            <p>3. <strong>They click "Join Car"</strong> on their dashboard and enter the code</p>
            <p>4. <strong>They get view access</strong> to your vehicle's service history and details</p>
          </div>
        </div>

        {/* Alternative Email Share (Simplified) */}
        <div className="border-t pt-4">
          <h4 className="font-semibold mb-2">Quick Email Share (If they have an account)</h4>
          <div className="flex gap-2">
            <Input
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              placeholder="Enter their email address"
              type="email"
              className="flex-1"
              data-testid="share-email-input"
            />
            <Button onClick={handleShare} className="bg-blue-600 hover:bg-blue-700" data-testid="share-email-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Note: They must already have a Track My Garage account for this to work.
          </p>
        </div>
      </div>

      {car.shared_users && car.shared_users.length > 0 && (
        <div className="space-y-4 mt-6 pt-6 border-t">
          <h4 className="font-semibold">Shared With</h4>
          <div className="space-y-2">
            {car.shared_users.map((share) => (
              <div key={share.user_email} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium">{share.user_name}</div>
                  <div className="text-sm text-gray-600">{share.user_email}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={share.permission === 'edit' ? 'default' : 'secondary'}>
                    {share.permission}
                  </Badge>
                  <Button
                    onClick={() => onRemoveShare(car.id, share.user_email)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    data-testid={`remove-share-${share.user_email}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Car Detail Component with Edit functionality and VIN display
const CarDetail = ({ car, serviceRecords, serviceTypes, onBack, onAddService, onShare, onRemoveShare, onDeleteCar, onEditCar, onTransferCar, onDeleteService, onDeleteFillup, analytics, userPreferences, isLoading = false }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const { user } = useAuth();

  const handleDeleteCar = async () => {
    try {
      await onDeleteCar(car.id);
      setShowDeleteDialog(false);
      onBack(); // Navigate back to dashboard
    } catch (error) {
      console.error('Error deleting car:', error);
    }
  };

  const isOwner = car.user_id === user?.id;
  const hasEditAccess = isOwner || car.shared_users?.some(share => share.user_id === user?.id && share.permission === 'edit');
  const hasEditPermission = hasEditAccess;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <Button 
          variant="ghost"
          onClick={onBack} 
          data-testid="back-to-dashboard-btn" 
          className="w-full md:w-auto !bg-gray-600 !text-white hover:!bg-gray-700 shadow"
        >
          ← Back to Dashboard
        </Button>
        <div className="flex flex-wrap gap-2 w-full md:w-auto justify-start md:justify-end">
          <Dialog>
            <DialogTrigger asChild>
              <Button 
                variant="ghost"
                className="!bg-purple-600 !text-white hover:!bg-purple-700 shadow" 
                data-testid="analytics-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Service Analytics</DialogTitle>
                <DialogDescription>
                  Service statistics for {car.make} {car.model}
                </DialogDescription>
              </DialogHeader>
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading analytics...</p>
                </div>
              ) : analytics && (
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-green-600">${analytics.total_cost}</div>
                      <div className="text-sm text-gray-600">Total Spent</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-blue-600">{analytics.total_services}</div>
                      <div className="text-sm text-gray-600">Total Services</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-2xl font-bold text-purple-600">${analytics.avg_cost_per_service}</div>
                      <div className="text-sm text-gray-600">Avg Cost</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <div className="text-xl font-bold text-orange-600">{analytics.most_common_service}</div>
                      <div className="text-sm text-gray-600">Most Common</div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </DialogContent>
          </Dialog>
          {hasEditAccess && (
            <Button 
              variant="ghost"
              className="!bg-blue-600 !text-white hover:!bg-blue-700 shadow" 
              onClick={onEditCar}
              data-testid="edit-car-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          <CarPhotoGallery 
            car={car}
            userPlan={user?.subscription?.plan_name || 'Free'}
            onPhotoAdd={async (photoData) => {
              // Handle photo addition to gallery
              try {
                const updatedGallery = [...(car.gallery || []), photoData];
                await axios.put(`${API}/cars/${car.id}`, { gallery: updatedGallery });
                toast.success('Photo added to gallery!');
                // Refresh car data
                window.location.reload();
              } catch (error) {
                console.error('Error adding photo:', error);
                // Check if it's a feature restriction error
                if (error.response?.status === 403 && error.response?.data?.detail?.error === 'feature_restricted') {
                  const errorDetail = error.response.data.detail;
                  toast.error(errorDetail.message || 'Photo Gallery is a Premium feature. Upgrade to access this feature.', {
                    duration: 5000,
                  });
                } else {
                  toast.error(error.response?.data?.detail || 'Failed to add photo to gallery');
                }
              }
            }}
            onPhotoDelete={async (photoId) => {
              // Handle photo deletion from gallery
              try {
                const updatedGallery = (car.gallery || []).filter(photo => photo.id !== photoId);
                await axios.put(`${API}/cars/${car.id}`, { gallery: updatedGallery });
                toast.success('Photo removed from gallery!');
                // Refresh car data
                window.location.reload();
              } catch (error) {
                console.error('Error removing photo:', error);
                // Check if it's a feature restriction error
                if (error.response?.status === 403 && error.response?.data?.detail?.error === 'feature_restricted') {
                  const errorDetail = error.response.data.detail;
                  toast.error(errorDetail.message || 'Photo Gallery is a Premium feature. Upgrade to access this feature.', {
                    duration: 5000,
                  });
                } else {
                  toast.error(error.response?.data?.detail || 'Failed to remove photo');
                }
              }
            }}
          />
          {isOwner && (
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  variant="ghost"
                  className="!bg-orange-600 !text-white hover:!bg-orange-700 shadow" 
                  data-testid="share-car-btn"
                >
                  <Share className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Share Vehicle</DialogTitle>
                  <DialogDescription>
                    Share this vehicle with family, friends, or mechanics
                  </DialogDescription>
                </DialogHeader>
                <CarSharingModal car={car} onShare={onShare} onRemoveShare={onRemoveShare} />
              </DialogContent>
            </Dialog>
          )}
          {isOwner && (
            <CarTransfer 
              car={car}
              onTransferComplete={onTransferCar}
              trigger={
                <Button 
                  variant="ghost"
                  className="!bg-purple-600 !text-white hover:!bg-purple-700 shadow" 
                  data-testid="transfer-car-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Transfer
                </Button>
              }
            />
          )}
          {isOwner && (
            <Button 
              variant="ghost"
              onClick={() => setShowShareLinkModal(true)}
              className="!bg-blue-500 !text-white hover:!bg-blue-600 shadow" 
              data-testid="public-share-link-btn"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Share Link
            </Button>
          )}
          {isOwner && (
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost"
                  className="!bg-red-600 !text-white hover:!bg-red-700 shadow" 
                  data-testid="delete-car-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this {car.make} {car.model}? This will permanently remove the vehicle and all its service records. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDeleteCar}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="confirm-delete-btn"
                  >
                    Delete Vehicle
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {!isOwner && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost"
                  className="!bg-orange-600 !text-white hover:!bg-orange-700 shadow" 
                  data-testid="leave-car-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Leave Car
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Leave Shared Vehicle</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to remove this {car.make} {car.model} from your garage? You will no longer have access to view this vehicle's details. The owner can re-share it with you later if needed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      onRemoveShare(car.id, user?.email);
                      onBack();
                    }}
                    className="bg-orange-600 hover:bg-orange-700"
                    data-testid="confirm-leave-btn"
                  >
                    Leave Vehicle
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button onClick={onAddService} className="bg-green-600 hover:bg-green-700" data-testid="add-service-btn">
            <Wrench className="w-4 h-4 mr-2" />
            Log Service
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Car className="w-6 h-6 mr-2 text-blue-600" />
            {car.make} {car.model} ({car.year})
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 ml-2"></div>
            )}
            {!isOwner && (
              <Badge variant="secondary" className="ml-2">
                Shared with you
              </Badge>
            )}
          </CardTitle>
          <CardDescription>Vehicle Details & Service History</CardDescription>
        </CardHeader>
        <CardContent>
          {car.photo && (
            <div className="mb-6">
              <img
                src={`data:${car.photo.content_type};base64,${car.photo.data}`}
                alt={`${car.make} ${car.model}`}
                className="w-full h-48 object-cover rounded-lg border"
              />
            </div>
          )}

          {/* Vehicle Information Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">Vehicle Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {car.vin && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium text-gray-600">VIN:</span>
                  <span className="font-mono text-gray-900">{car.vin}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium text-gray-600">Make & Model:</span>
                <span className="text-gray-900">{car.make} {car.model}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium text-gray-600">Year:</span>
                <span className="text-gray-900">{car.year}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="font-medium text-gray-600">Owner:</span>
                <span className="text-gray-900">{car.owner_name}</span>
              </div>
              {car.color && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium text-gray-600">Color:</span>
                  <span className="text-gray-900">{car.color}</span>
                </div>
              )}
              {car.license_plate && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium text-gray-600">License Plate:</span>
                  <span className="text-gray-900">{car.license_plate}</span>
                </div>
              )}
              {car.engine_type && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium text-gray-600">Engine Type:</span>
                  <span className="text-gray-900">{car.engine_type}</span>
                </div>
              )}
              {car.engine_size && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium text-gray-600">Engine Size:</span>
                  <span className="text-gray-900">{car.engine_size}</span>
                </div>
              )}
              {car.transmission && (
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium text-gray-600">Transmission:</span>
                  <span className="text-gray-900">{car.transmission}</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Modifications Section */}
          {car.modifications && car.modifications.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-green-600" />
                Modifications & Upgrades
              </h3>
              <div className="flex flex-wrap gap-2">
                {car.modifications.map((mod, index) => (
                  <Badge key={index} className="bg-green-100 text-green-800 border-green-200">
                    {mod}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{car.current_odometer.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Current {getDistanceUnitLong(userPreferences?.measurement_system)}</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{serviceRecords.length}</div>
              <div className="text-sm text-gray-600">Service Records</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{car.owner_name}</div>
              <div className="text-sm text-gray-600">Owner</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-2xl font-bold text-orange-600">
                {car.shared_users ? car.shared_users.length : 0}
              </div>
              <div className="text-sm text-gray-600">Shared Users</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Wrench className="w-5 h-5 mr-2 text-green-600" />
            Service History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading service history...</p>
            </div>
          ) : serviceRecords.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>No service records yet. Start by logging your first service!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {serviceRecords.map((record) => (
                <div key={record.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors" data-testid={`service-record-${record.id}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-2">
                      <div className="text-sm text-gray-500 flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(record.service_date).toLocaleDateString()}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-1" />
                        {formatDistance(record.odometer_reading, userPreferences?.measurement_system)}
                      </div>
                      {record.total_cost && (
                        <div className="flex items-center text-green-600 font-semibold">
                          <DollarSign className="w-4 h-4 mr-1" />
                          ${record.total_cost.toFixed(2)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        {record.performed_by && (
                          <span>By: {record.performed_by}</span>
                        )}
                        {record.location && (
                          <span>@ {record.location}</span>
                        )}
                      </div>
                      {(isOwner || hasEditPermission) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-service-${record.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Service Record</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this service record from {new Date(record.service_date).toLocaleDateString()}? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDeleteService(record.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete Record
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {record.tasks.map((task) => (
                        <Badge key={task.id} className="bg-blue-100 text-blue-800 border-blue-200">
                          {task.task_type}
                          {task.cost && <span className="ml-1">(${task.cost})</span>}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="space-y-2">
                      {record.tasks.map((task) => (
                        <div key={task.id} className="pl-4 border-l-2 border-gray-200">
                          <h5 className="font-medium text-gray-900">{task.description}</h5>
                          {task.notes && (
                            <p className="text-gray-600 text-sm">{task.notes}</p>
                          )}
                          {task.photos && task.photos.length > 0 && (
                            <div className="flex gap-2 mt-2">
                              {task.photos.map((photo) => (
                                <img
                                  key={photo.id}
                                  src={`data:${photo.content_type};base64,${photo.data}`}
                                  alt={photo.filename}
                                  className="w-16 h-16 object-cover rounded border cursor-pointer hover:opacity-80"
                                  onClick={() => {
                                    // Open photo in new window for larger view
                                    const newWindow = window.open();
                                    newWindow.document.write(`<img src="data:${photo.content_type};base64,${photo.data}" style="max-width: 100%; height: auto;" />`);
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    {record.notes && (
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-gray-700 text-sm"><strong>Service Notes:</strong> {record.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fuel Tracking Section */}
      <FuelFillUp 
        car={car} 
        userPreferences={userPreferences}
        isOwner={isOwner}
        hasEditPermission={hasEditPermission}
        userPlan={user?.subscription?.plan_name || 'Free'}
        onUpdate={() => {
          // Refresh car data when odometer is updated
          window.location.reload(); // Simple refresh for now
        }}
      />

      {/* Parts List */}
      <PartsList 
        car={car}
        isOwner={isOwner}
        hasEditPermission={hasEditPermission}
        userPlan={user?.subscription?.plan_name || 'Free'}
      />
      
      {/* Share Link Modal */}
      <ShareLinkModal 
        car={car}
        isOpen={showShareLinkModal}
        onClose={() => setShowShareLinkModal(false)}
        userPlan={user?.subscription?.plan_name || 'Free'}
      />
    </div>
  );
};

// Multi-Task Service Form (same as before)
const AddServiceForm = ({ car, serviceTypes, onSubmit, onCancel, userPreferences }) => {
  const [formData, setFormData] = useState({
    service_date: new Date().toISOString().split('T')[0],
    odometer_reading: car.current_odometer,
    performed_by: '',
    location: '',
    notes: '',
    tasks: [{ 
      task_type: '', 
      description: '', 
      cost: '', 
      notes: '', 
      photos: [],
      next_due_kilometers: '',
      next_due_days: ''
    }]
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting) return;
    
    // Validate at least one task
    if (formData.tasks.length === 0 || !formData.tasks[0].task_type) {
      toast.error('Please add at least one service task');
      return;
    }

    const submitData = {
      car_id: car.id,
      service_date: new Date(formData.service_date).toISOString(),
      odometer_reading: parseInt(formData.odometer_reading),
      performed_by: formData.performed_by || null,
      location: formData.location || null,
      notes: formData.notes || null,
      tasks: formData.tasks.map(task => ({
        task_type: task.task_type,
        description: task.description,
        cost: task.cost ? parseFloat(task.cost) : null,
        notes: task.notes || null,
        photos: task.photos || [],
        next_due_kilometers: task.next_due_kilometers ? parseInt(task.next_due_kilometers) : null,
        next_due_days: task.next_due_days ? parseInt(task.next_due_days) : null
      }))
    };
    
    setIsSubmitting(true);
    try {
      await onSubmit(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTaskChange = (index, field, value) => {
    const newTasks = [...formData.tasks];
    newTasks[index] = { ...newTasks[index], [field]: value };
    setFormData(prev => ({ ...prev, tasks: newTasks }));
  };

  const handleTaskPhotoAdd = (index, photoData) => {
    const newTasks = [...formData.tasks];
    if (!newTasks[index].photos) {
      newTasks[index].photos = [];
    }
    newTasks[index].photos.push(photoData);
    setFormData(prev => ({ ...prev, tasks: newTasks }));
  };

  const handleTaskPhotoRemove = (taskIndex, photoIndex) => {
    const newTasks = [...formData.tasks];
    newTasks[taskIndex].photos.splice(photoIndex, 1);
    setFormData(prev => ({ ...prev, tasks: newTasks }));
  };

  const addTask = () => {
    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, { 
        task_type: '', 
        description: '', 
        cost: '', 
        notes: '', 
        photos: [],
        next_due_kilometers: '',
        next_due_days: ''
      }]
    }));
  };

  const removeTask = (index) => {
    if (formData.tasks.length > 1) {
      const newTasks = formData.tasks.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, tasks: newTasks }));
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Wrench className="w-5 h-5 mr-2 text-green-600" />
          Log Service for {car.make} {car.model}
        </CardTitle>
        <CardDescription>
          Record maintenance and repairs with multiple tasks and photos
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="service_date">Service Date *</Label>
              <Input
                id="service_date"
                type="date"
                value={formData.service_date}
                onChange={(e) => handleChange('service_date', e.target.value)}
                required
                data-testid="service-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="odometer_reading">Odometer Reading *</Label>
              <Input
                id="odometer_reading"
                type="number"
                value={formData.odometer_reading}
                onChange={(e) => handleChange('odometer_reading', e.target.value)}
                min="0"
                required
                data-testid="service-odometer-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="performed_by">Performed By</Label>
              <Input
                id="performed_by"
                value={formData.performed_by}
                onChange={(e) => handleChange('performed_by', e.target.value)}
                placeholder="Mechanic or shop name"
                data-testid="service-performer-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="Service location"
                data-testid="service-location-input"
              />
            </div>
          </div>

          {/* Tasks Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Service Tasks</Label>
              <Button
                type="button"
                onClick={addTask}
                variant="outline"
                size="sm"
                className="text-green-600 border-green-200 hover:bg-green-50"
                data-testid="add-task-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Task
              </Button>
            </div>

            {formData.tasks.map((task, index) => (
              <Card key={index} className="p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-medium">Task #{index + 1}</h4>
                  {formData.tasks.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removeTask(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      data-testid={`remove-task-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Service Type *</Label>
                    <Select
                      value={task.task_type}
                      onValueChange={(value) => handleTaskChange(index, 'task_type', value)}
                      required
                    >
                      <SelectTrigger data-testid={`task-type-select-${index}`}>
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Cost ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={task.cost}
                      onChange={(e) => handleTaskChange(index, 'cost', e.target.value)}
                      placeholder="Task cost"
                      data-testid={`task-cost-${index}`}
                    />
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label>Description *</Label>
                  <Input
                    value={task.description}
                    onChange={(e) => handleTaskChange(index, 'description', e.target.value)}
                    placeholder="Brief description of this task"
                    required
                    data-testid={`task-description-${index}`}
                  />
                </div>

                <div className="space-y-2 mt-4">
                  <Label>Task Notes</Label>
                  <Textarea
                    value={task.notes}
                    onChange={(e) => handleTaskChange(index, 'notes', e.target.value)}
                    placeholder="Additional notes for this task"
                    rows={2}
                    data-testid={`task-notes-${index}`}
                  />
                </div>

                {/* Service Reminder Settings */}
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center mb-3">
                    <Clock className="w-4 h-4 mr-2 text-blue-600" />
                    <Label className="text-blue-800 font-medium">Service Reminder Settings</Label>
                  </div>
                  <p className="text-sm text-blue-700 mb-3">
                    Set when this service should be performed again (optional)
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Next Due ({getDistanceUnitLong(userPreferences?.measurement_system)})</Label>
                      <Input
                        type="number"
                        value={task.next_due_kilometers}
                        onChange={(e) => handleTaskChange(index, 'next_due_kilometers', e.target.value)}
                        placeholder="e.g., 5000"
                        min="1"
                        data-testid={`task-next-km-${index}`}
                      />
                      <p className="text-xs text-gray-500">
                        How many {getDistanceUnitLong(userPreferences?.measurement_system)} until this service is needed again
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Next Due (Days)</Label>
                      <Input
                        type="number"
                        value={task.next_due_days}
                        onChange={(e) => handleTaskChange(index, 'next_due_days', e.target.value)}
                        placeholder="e.g., 365"
                        min="1"
                        data-testid={`task-next-days-${index}`}
                      />
                      <p className="text-xs text-gray-500">
                        How many days until this service is needed again
                      </p>
                    </div>
                  </div>
                  
                  <div className="mt-2 text-xs text-blue-600">
                    💡 Example: Oil change every 5,000 {getDistanceUnit(userPreferences?.measurement_system)} or 6 months (180 days)
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label>Task Photos</Label>
                  <PhotoUpload
                    onPhotoSelect={(photoData) => photoData && handleTaskPhotoAdd(index, photoData)}
                    multiple={false}
                  />
                  
                  {task.photos && task.photos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {task.photos.map((photo, photoIndex) => (
                        <div key={photoIndex} className="relative">
                          <img
                            src={`data:${photo.content_type};base64,${photo.data}`}
                            alt={photo.filename}
                            className="w-16 h-16 object-cover rounded border"
                          />
                          <Button
                            onClick={() => handleTaskPhotoRemove(index, photoIndex)}
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 h-6 w-6 p-0"
                            data-testid={`remove-task-photo-${index}-${photoIndex}`}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* Overall Service Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Overall Service Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="General notes about this service visit"
              rows={3}
              data-testid="service-notes-textarea"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <Button 
              type="submit" 
              className="flex-1 bg-green-600 hover:bg-green-700" 
              data-testid="save-service-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Service Record'
              )}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel} 
              className="flex-1" 
              data-testid="cancel-service-btn"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const currentPath = window.location.pathname;

  // Handle public car view route (no authentication required)
  if (currentPath.startsWith('/public-car/')) {
    const PublicCarView = require('./components/PublicCarView').default;
    return <PublicCarView />;
  }

  // Handle password reset routes (public)
  if (currentPath === '/forgot-password') {
    return <ForgotPasswordPage />;
  }
  
  if (currentPath === '/reset-password') {
    return <ResetPasswordPage />;
  }

  // Handle admin test route
  if (currentPath === '/admin-test') {
    const AdminTestPage = require('./AdminTestPage').default;
    return <AdminTestPage />;
  }

  // Handle admin direct test route
  if (currentPath === '/admin-direct-test') {
    const AdminDirectTest = require('./AdminDirectTest').default;
    return <AdminDirectTest />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Car className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-bounce" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  return children;
};

// Main App Component
const AppContent = () => {
  const [cars, setCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [carDataLoading, setCarDataLoading] = useState(false);
  const [carDataCache, setCarDataCache] = useState({});
  const [serviceRecords, setServiceRecords] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [sharingRequests, setSharingRequests] = useState([]);
  const [currentView, setCurrentView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [userPreferences, setUserPreferences] = useState({
    theme: 'default',
    measurement_system: 'metric',
    custom_background: null
  });
  const { user } = useAuth();

  // Simple text visibility fix for any remaining edge cases
  useEffect(() => {
    const timer = setTimeout(() => {
      // Simple fix for any remaining light text
      const lightTextElements = document.querySelectorAll('*');
      lightTextElements.forEach(element => {
        const style = window.getComputedStyle(element);
        if (style.color.includes('250, 250, 250')) {
          element.style.setProperty('color', '#0a0a0a', 'important');
        }
      });
    }, 500);
    
    return () => clearTimeout(timer);
  }, [userPreferences.theme]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [carsResponse, serviceTypesResponse, preferencesResponse, requestsResponse] = await Promise.all([
          axios.get(`${API}/cars`),
          axios.get(`${API}/service-types`),
          axios.get(`${API}/user/preferences`).catch(() => null), // Don't fail if preferences don't exist
          axios.get(`${API}/cars/sharing-requests`)
        ]);
        
        setCars(carsResponse.data);
        setServiceTypes(serviceTypesResponse.data);
        setSharingRequests(requestsResponse.data);
        
        if (preferencesResponse) {
          setUserPreferences(preferencesResponse.data);
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast.error('Failed to load data');
        // Set empty sharing requests if the call fails
        setSharingRequests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Handle invitation accept/decline from URL parameters
  useEffect(() => {
    const handleInvitationResponse = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const action = window.location.pathname;

      if (token && (action.includes('/invitation/accept') || action.includes('/invitation/decline'))) {
        try {
          const endpoint = action.includes('/accept') ? 'accept' : 'decline';
          const response = await axios.get(`${API}/invitation/${endpoint}?token=${token}`);
          
          if (response.data.success) {
            if (endpoint === 'accept') {
              toast.success(response.data.message || 'Vehicle added to your account successfully!');
              // Refresh cars list
              const carsResponse = await axios.get(`${API}/cars`);
              setCars(carsResponse.data);
              
              // Navigate to the car if provided
              if (response.data.car_id) {
                const newCar = carsResponse.data.find(car => car.id === response.data.car_id);
                if (newCar) {
                  setSelectedCar(newCar);
                  setCurrentView('carDetail');
                }
              }
            } else {
              toast.success(response.data.message || 'Invitation declined');
            }
          } else {
            toast.error(response.data.message || 'Failed to process invitation');
          }
        } catch (error) {
          console.error('Error processing invitation:', error);
          toast.error(error.response?.data?.detail || 'Failed to process invitation');
        }
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    handleInvitationResponse();
  }, []);

  // Fetch service records and analytics for selected car
  useEffect(() => {
    if (selectedCar) {
      fetchCarData(selectedCar.id);
    }
  }, [selectedCar]);
  // Prefetch cars data when dashboard loads
  useEffect(() => {
    if (currentView === 'dashboard' && cars.length > 0) {
      // Prefetch data for the first few cars in background
      const prefetchCars = cars.slice(0, 3); // Prefetch first 3 cars
      prefetchCars.forEach((car, index) => {
        // Stagger the prefetch requests to avoid overwhelming the server
        setTimeout(() => {
          if (!carDataCache[car.id] || (Date.now() - carDataCache[car.id].timestamp > 60000)) {
            fetchCarDataSilently(car.id);
          }
        }, index * 200); // 200ms apart
      });
    }
  }, [currentView, cars]);
  
  // Silent fetch for prefetching (no loading states)
  const fetchCarDataSilently = async (carId) => {
    try {
      // Fetch service records and analytics separately to handle feature restrictions gracefully
      const serviceResponse = await axios.get(`${API}/cars/${carId}/service-records`);
      
      // Try to fetch analytics, but don't fail if user doesn't have access
      let analyticsData = {};
      try {
        const analyticsResponse = await axios.get(`${API}/cars/${carId}/analytics`);
        analyticsData = analyticsResponse.data;
      } catch (analyticsError) {
        // Analytics might be restricted by subscription plan - that's okay
        if (analyticsError.response?.status === 403) {
          console.log('Analytics not available for this subscription plan');
        } else {
          console.log('Error fetching analytics:', analyticsError);
        }
      }
      
      // Cache the data silently
      setCarDataCache(prev => ({
        ...prev,
        [carId]: {
          serviceRecords: serviceResponse.data,
          analytics: analyticsData,
          timestamp: Date.now()
        }
      }));
    } catch (error) {
      // Silently fail - this is just prefetching
      console.log('Prefetch failed for car:', carId);
    }
  };

  const fetchCarData = async (carId, useCache = false) => {
    // Check cache first if requested
    if (useCache && carDataCache[carId]) {
      const cached = carDataCache[carId];
      const cacheAge = Date.now() - cached.timestamp;
      
      // Use cache if less than 30 seconds old
      if (cacheAge < 30000) {
        setServiceRecords(cached.serviceRecords);
        setAnalytics(cached.analytics);
        setCarDataLoading(false);
        return;
      }
    }
    
    setCarDataLoading(true);
    
    try {
      // Fetch service records (available to all users)
      const serviceResponse = await axios.get(`${API}/cars/${carId}/service-records`);
      const serviceData = serviceResponse.data;
      setServiceRecords(serviceData);
      
      // Try to fetch analytics separately - may be restricted by subscription
      let analyticsData = {};
      try {
        const analyticsResponse = await axios.get(`${API}/cars/${carId}/analytics`);
        analyticsData = analyticsResponse.data;
        setAnalytics(analyticsData);
      } catch (analyticsError) {
        // Analytics might be restricted by subscription plan - that's okay
        if (analyticsError.response?.status === 403) {
          console.log('Advanced analytics not available for this subscription plan');
          setAnalytics({}); // Set empty analytics, but don't show error
        } else {
          console.error('Error fetching analytics:', analyticsError);
          setAnalytics({});
        }
      }
      
      // Cache the data
      setCarDataCache(prev => ({
        ...prev,
        [carId]: {
          serviceRecords: serviceData,
          analytics: analyticsData,
          timestamp: Date.now()
        }
      }));
      
    } catch (error) {
      console.error('Error fetching car data:', error);
      toast.error('Failed to load car data');
      // Set empty data on error to avoid showing stale data
      setServiceRecords([]);
      setAnalytics({});
    } finally {
      setCarDataLoading(false);
    }
  };

  const handleSettingsChange = (newPreferences) => {
    setUserPreferences(newPreferences);
  };

  const handleAddCar = async (carData) => {
    // Check subscription limits before adding
    const subscription = user?.subscription;
    const maxCars = subscription?.max_cars ?? 1;
    
    // Check if user has reached their car limit (skip if unlimited: -1)
    if (maxCars !== -1 && cars.length >= maxCars) {
      toast.error(`You've reached the limit of ${maxCars} car(s) on the ${subscription?.plan_name || 'Free'} plan. Please upgrade your subscription.`);
      setCurrentView('subscriptions');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/cars`, carData);
      setCars([...cars, response.data]);
      setCurrentView('dashboard');
      toast.success('Vehicle added successfully!');
    } catch (error) {
      console.error('Error adding car:', error);
      const errorDetail = error.response?.data?.detail || '';
      if (error.response?.status === 403 && (errorDetail.includes('maximum number of cars') || errorDetail.includes('subscription limit') || errorDetail.includes('reached'))) {
        toast.error(errorDetail || 'You have reached your subscription limit. Please upgrade to add more cars.');
        setCurrentView('subscriptions');
      } else {
        toast.error(errorDetail || 'Failed to add vehicle');
      }
    }
  };

  const handleEditCar = async (carData) => {
    try {
      const response = await axios.put(`${API}/cars/${selectedCar.id}`, carData);
      const updatedCar = response.data;
      
      // Update the car in the cars list
      setCars(cars.map(car => car.id === updatedCar.id ? updatedCar : car));
      
      // Update the selected car
      setSelectedCar(updatedCar);
      
      setCurrentView('carDetail');
      toast.success('Vehicle updated successfully!');
    } catch (error) {
      console.error('Error updating car:', error);
      toast.error('Failed to update vehicle');
    }
  };

  const handleSelectCar = (carOrId) => {
    // Handle both car object and car ID
    let car;
    if (typeof carOrId === 'string') {
      // If it's a string (car_id), find the car object
      car = cars.find(c => c.id === carOrId);
      if (!car) {
        console.error('Car not found with ID:', carOrId);
        toast.error('Car not found');
        return;
      }
    } else {
      // If it's already a car object, use it
      car = carOrId;
    }
    
    // Check if we have cached data for this car
    const cached = carDataCache[car.id];
    const hasCachedData = cached && (Date.now() - cached.timestamp < 30000);
    
    if (hasCachedData) {
      // Use cached data immediately for instant loading
      setServiceRecords(cached.serviceRecords);
      setAnalytics(cached.analytics);
      setCarDataLoading(false);
    } else {
      // Clear previous car data to prevent showing stale data
      setServiceRecords([]);
      setAnalytics({});
      setCarDataLoading(true);
    }
    
    // Set new car and view
    setSelectedCar(car);
    setCurrentView('carDetail');
    
    // Fetch fresh data (either to populate if no cache, or to refresh cache)
    if (!hasCachedData) {
      fetchCarData(car.id);
    } else {
      // Fetch in background to refresh cache
      setTimeout(() => fetchCarData(car.id, true), 100);
    }
  };

  const handleDeleteCar = async (carId) => {
    try {
      const response = await axios.delete(`${API}/cars/${carId}`);
      setCars(cars.filter(car => car.id !== carId));
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error deleting car:', error);
      toast.error('Failed to delete vehicle');
      throw error;
    }
  };

  const handleTransferCar = async () => {
    // Transfer complete - refresh cars and go back to dashboard
    try {
      const response = await axios.get(`${API}/cars`);
      setCars(response.data);
      setCurrentView('dashboard');
      setSelectedCar(null);
      toast.success('Returning to dashboard - car has been transferred');
    } catch (error) {
      console.error('Error refreshing cars:', error);
      // Still go back to dashboard even if refresh fails
      setCurrentView('dashboard');
      setSelectedCar(null);
    }
  };

  const handleDeleteService = async (recordId) => {
    try {
      await axios.delete(`${API}/service-records/${recordId}`);
      setServiceRecords(serviceRecords.filter(record => record.id !== recordId));
      toast.success('Service record deleted successfully!');
      
      // Refresh car analytics after deletion
      await fetchCarData(selectedCar.id);
    } catch (error) {
      console.error('Error deleting service record:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete service record');
    }
  };

  const handleDeleteFillup = async (fillupId) => {
    try {
      await axios.delete(`${API}/cars/${selectedCar.id}/fuel-fillups/${fillupId}`);
      toast.success('Fuel fill-up deleted successfully!');
      // Refresh car data
      await fetchCarData(selectedCar.id);
    } catch (error) {
      console.error('Error deleting fill-up:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete fill-up');
    }
  };

  const handleAddService = async (serviceData) => {
    try {
      const response = await axios.post(`${API}/service-records`, serviceData);
      setServiceRecords([response.data, ...serviceRecords]);
      
      // Update car's current odometer if needed
      if (serviceData.odometer_reading > selectedCar.current_odometer) {
        const updatedCar = { ...selectedCar, current_odometer: serviceData.odometer_reading };
        setSelectedCar(updatedCar);
        setCars(cars.map(car => car.id === updatedCar.id ? updatedCar : car));
      }
      
      // Invalidate cache for this car since we added new data
      setCarDataCache(prev => {
        const newCache = {...prev};
        delete newCache[selectedCar.id];
        return newCache;
      });
      
      // Refresh analytics
      fetchCarData(selectedCar.id);
      
      setCurrentView('carDetail');
      toast.success('Service record added successfully!');
    } catch (error) {
      console.error('Error adding service record:', error);
      toast.error('Failed to add service record');
    }
  };

  const handleShareCar = async (carId, email, permission) => {
    try {
      const response = await axios.post(`${API}/cars/${carId}/share`, {
        car_id: carId,
        user_email: email,
        permission: permission
      });
      
      // Refresh car data to show updated sharing info
      const updatedCar = await axios.get(`${API}/cars/${carId}`);
      setSelectedCar(updatedCar.data);
      setCars(cars.map(car => car.id === carId ? updatedCar.data : car));
      
      toast.success('Vehicle shared successfully!');
    } catch (error) {
      console.error('Error sharing car:', error);
      toast.error('Failed to share vehicle');
    }
  };

  const handleRemoveShare = async (carId, userEmail) => {
    try {
      const response = await axios.delete(`${API}/cars/${carId}/share/${encodeURIComponent(userEmail)}`);
      
      // Check if current user is leaving the car (self-removal)
      const isSelfRemoval = userEmail === user?.email;
      
      if (isSelfRemoval) {
        // Remove the car from the user's car list entirely
        setCars(cars.filter(car => car.id !== carId));
        toast.success(response.data.message || 'You have left this vehicle');
      } else {
        // Owner removing someone else - refresh car data to show updated shared users
        const updatedCar = await axios.get(`${API}/cars/${carId}`);
        setSelectedCar(updatedCar.data);
        setCars(cars.map(car => car.id === carId ? updatedCar.data : car));
        toast.success(response.data.message || 'Access removed successfully!');
      }
    } catch (error) {
      console.error('Error removing share:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove access');
    }
  };

  const handleJoinCar = async (joinData) => {
    try {
      const response = await axios.post(`${API}/cars/request-access-by-code`, {
        share_code: joinData.share_code,
        message: "Hi! I'd like to access your vehicle's maintenance records."
      });
      toast.success(response.data.message);
    } catch (error) {
      console.error('Error requesting car access:', error);
      toast.error(error.response?.data?.detail || 'Failed to send access request');
    }
  };
  const handleSharingRequestResponse = async (requestId, action, permission = 'view') => {
    try {
      const response = await axios.post(`${API}/cars/sharing-requests/respond`, {
        request_id: requestId,
        action: action,
        permission: permission
      });
      
      toast.success(response.data.message);
      
      // Remove the request from the list
      setSharingRequests(prev => prev.filter(req => req.id !== requestId));
      
      // If approved, refresh cars list to show the updated shared users
      if (action === 'approve') {
        const carsResponse = await axios.get(`${API}/cars`);
        setCars(carsResponse.data);
      }
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${action} request`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-bounce mb-4">
            <Logo size="giant" />
          </div>
          <p className="text-gray-600">Loading Track My Garage...</p>
        </div>
      </div>
    );
  }

  const getThemeClasses = () => {
    switch (userPreferences.theme) {
      case 'dark':
        return 'min-h-screen bg-background text-foreground';
      case 'garage':
        return 'min-h-screen bg-background text-foreground bg-cover bg-center bg-no-repeat bg-fixed';
      case 'custom':
        return userPreferences.custom_background 
          ? `min-h-screen bg-background text-foreground bg-cover bg-center bg-no-repeat` 
          : 'min-h-screen bg-background text-foreground';
      default:
        return 'min-h-screen bg-background text-foreground';
    }
  };

  const getThemeDataAttribute = () => {
    switch (userPreferences.theme) {
      case 'dark':
        return 'dark';
      case 'garage':
        return 'garage';
      case 'custom':
        return userPreferences.custom_background ? 'custom' : 'default';
      default:
        return 'default';
    }
  };

  const getBackgroundStyle = () => {
    if (userPreferences.theme === 'garage') {
      return {
        backgroundImage: 'url("https://customer-assets.emergentagent.com/job_maintenance-pal-1/artifacts/8lszdnzs_ChatGPT%20Image%20Sep%2025%2C%202025%2C%2008_56_15%20PM.png")',
        backgroundAttachment: 'fixed'
      };
    }
    if (userPreferences.theme === 'custom' && userPreferences.custom_background) {
      return {
        backgroundImage: `url(data:${userPreferences.custom_background.content_type};base64,${userPreferences.custom_background.data})`
      };
    }
    return {};
  };

  return (
    <div className={getThemeClasses()} style={getBackgroundStyle()} data-theme={getThemeDataAttribute()}>
      {userPreferences.theme === 'garage' && (
        <div className="fixed inset-0 bg-black bg-opacity-30 pointer-events-none z-0"></div>
      )}
      <div className="relative z-10">
        <Header onSettingsChange={handleSettingsChange} />

        <div className="container mx-auto px-4 py-2">
          <div className="space-y-6">
        {currentView === 'dashboard' && (
          <CarDashboard
            cars={cars}
            onAddCar={() => setCurrentView('addCar')}
            onSelectCar={handleSelectCar}
            onJoinCar={handleJoinCar}
            onShowSubscriptions={() => setCurrentView('subscriptions')}
            onShowAdmin={() => setCurrentView('admin')}
            user={user}
            userPreferences={userPreferences}
            sharingRequests={sharingRequests}
            onRequestResponse={handleSharingRequestResponse}
          />
        )}

        {currentView === 'subscriptions' && (
          <SubscriptionPlans
            user={user}
            onBack={() => setCurrentView('dashboard')}
          />
        )}

        {currentView === 'admin' && (user?.role === 'admin' || user?.role === 'owner') && (
          <AdminDashboard
            user={user}
            onBack={() => setCurrentView('dashboard')}
          />
        )}

        {currentView === 'addCar' && (
          <CarForm
            onSubmit={handleAddCar}
            onCancel={() => setCurrentView('dashboard')}
            userPreferences={userPreferences}
          />
        )}

        {currentView === 'editCar' && selectedCar && (
          <CarForm
            car={selectedCar}
            onSubmit={handleEditCar}
            onCancel={() => setCurrentView('carDetail')}
            userPreferences={userPreferences}
          />
        )}

        {currentView === 'carDetail' && selectedCar && (
          <CarDetail
            car={selectedCar}
            serviceRecords={serviceRecords}
            serviceTypes={serviceTypes}
            analytics={analytics}
            isLoading={carDataLoading}
            onBack={() => setCurrentView('dashboard')}
            onAddService={() => setCurrentView('addService')}
            onEditCar={() => setCurrentView('editCar')}
            onShare={handleShareCar}
            onRemoveShare={handleRemoveShare}
            onDeleteCar={handleDeleteCar}
            onTransferCar={handleTransferCar}
            onDeleteService={handleDeleteService}
            onDeleteFillup={handleDeleteFillup}
            userPreferences={userPreferences}
          />
        )}

        {currentView === 'addService' && selectedCar && (
          <AddServiceForm
            car={selectedCar}
            serviceTypes={serviceTypes}
            onSubmit={handleAddService}
            onCancel={() => setCurrentView('carDetail')}
            userPreferences={userPreferences}
          />
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <AppContent />
      </ProtectedRoute>
      <Toaster />
    </AuthProvider>
  );
}

export default App;